import { Component, OnInit } from '@angular/core';
import { FundPerformance } from '../../../../dto/FundPerformance';
import { firstValueFrom } from 'rxjs';
import { NotificationsService } from '../../../../../../core/service/notifications.service';
import {
  FpBulkUploadTemplate,
} from '../../../../../excel/templates/fund-performnace-hight-level/bulk-upload/FpBulkUploadTemplate';
import { FundPerformanceBackendService } from '../../../../service/fund-performance-backend.service';
import { OrganizationsService } from '../../../../../organization/components/service/organizations.service';
import { HorNavbarService } from '../../../../../../core/service/hor-navbar.service';
import { DefaultBulkUploadComponent } from '../../../../../bulk-upload/default-bulk-upload/default-bulk-upload.component';
import {
  DefaultBulkUploadShow,
  DefaultBulkUploadShowValidationMessage,
} from '../../../../../bulk-upload/pojo/default-bulk-upload-pojo';

@Component({
  selector: 'app-modal-fp-bulk-upload',
  templateUrl: '../../../../../bulk-upload/default-bulk-upload/default-bulk-upload.component.html',
  styleUrls: ['../../../../../bulk-upload/default-bulk-upload/default-bulk-upload.component.scss'],
})
export class ModalFpBulkUploadComponent extends DefaultBulkUploadComponent implements OnInit {
  show: ShowFPBulkUpload;
  fps: FundPerformance[];
  parsedFPs: FundPerformance[];

  constructor(
    orgService: OrganizationsService,
    notificationService: NotificationsService,
    private fpService: FundPerformanceBackendService,
    private navbarService: HorNavbarService) {
    super(orgService, notificationService);
  }

  override initVariables(): void {
    this.show = {
      title: {
        header: 'Fund performance bulk upload',
        info: {
          main: 'Your fund performance data will be updated.',
          sub: 'Portfolio performance data will be replaced with values as presented below.',
        },
      },
      periods: undefined,
      validationMessage: undefined,
    };
  }

  protected fetchDataAfterOrgInit(): void {
    this.getFundPerformances();
  }

  /**
   * Downloads Excel and changes the style of the download/upload buttons.
   */
  override downloadExcel(): void {
    new FpBulkUploadTemplate().export(this.fps, this.selectedOrg.selectedFund);
    super.downloadExcel();
  }

  // eslint-disable-next-line @typescript-eslint/require-await
  override async parseDataAfterImport(data: unknown[]): Promise<void> {
    this.parsedFPs = new FpBulkUploadTemplate().import(data);
  }

  /**
   * After user clicks continue, we want to be sure that both: <br>
   * 1) new Fund Performances <br>
   * 2) existing FPs (same period) <br>
   * are created/updated. Only then we update hornavbar and close the modal.
   */
  override async confirm(): Promise<void> {
    this.is.applyActivated = false;
    await this.createNewFP();
    await this.updateExistingFP();
    this.navbarService.fetchAllFundPerformancesFromBackend();
    this.close(true);
  }

  override prepareValidationError(data: FundPerformance, message: DefaultBulkUploadShowValidationMessage): void {
    message.bottomText = `In the ${data.period.toString()} period${message.bottomText}.`;
  }


  override assertSufficientData(): void {
    if (!this.parsedFPs.length) {
      super.showInsufficientDataAndClose();
    }
  }

  override mapData(): void {
    this.show.periods =
      this.parsedFPs.map((parsedFP) => ({
        name: parsedFP.period.toString(),
        isNew: !Boolean(this.fps.find((fp) => fp.period === parsedFP.period)),
      }));
  }


  /**
   * Validation:
   * 0) Period is not provided
   * 1) Duplicate periods
   * 2) Commitment and Contribution must be positive
   * 3) OpEx, MgM, aggregatedOrgExpenses and Carried interest must be negative
   * 4) Contribution <= Commitment
   * 5) No Commitment
   */
  override validateData(): void {
    const fps = this.parsedFPs;
    this.checkError(fps.filter((fp) => !fp.period), 'Period could not be parsed');
    this.checkError(fps.filter((fp) => fps
      .filter((_fp) => fp.period === _fp.period)
      .length > 1), 'Duplicate periods:');
    this.checkError(fps.filter((fp) => fp.totalCommitment && fp.totalCommitment < 0), 'Commitment must be positive');
    this.checkError(fps.filter((fp) => fp.contribution && fp.contribution < 0), 'Contribution must be positive');
    this.checkError(fps.filter((fp) => fp.totalExpenses && fp.totalExpenses > 0), 'OpEx must be negative');
    this.checkError(fps.filter((fp) => fp.mgmFees && fp.mgmFees > 0), 'Management fees must be negative');
    this.checkError(fps.filter((fp) => fp.aggregatedOrgExpenses && fp.aggregatedOrgExpenses > 0), 'Organizational expenses must be negative');
    this.checkError(fps.filter((fp) => fp.carryInterest && fp.carryInterest > 0), 'Carried interest must be negative');
    this.checkError(fps.filter((fp) => !fp.totalCommitment), 'No Total commitment found');
  }

  private async createNewFP(): Promise<void> {
    const createdFps = this.parsedFPs
      .filter((parsedFP) => !this.fps.find((fp) => (parsedFP.period === fp.period)));
    if (createdFps.length) {
      await firstValueFrom(this.fpService.create(createdFps, this.selectedOrg.selectedFund.uuid));
    }
  }

  private async updateExistingFP(): Promise<void> {
    const updatingFps = this.prepareExistingFps();
    for (const fp of updatingFps) {
      await this.fpService.update(fp);
    }
  }

  private prepareExistingFps(): FundPerformance[] {
    const persistedFPs = this.fps
      .filter((persistedFP) => this.parsedFPs.find((parsedFP) => parsedFP.period === persistedFP.period));

    persistedFPs.forEach((persistedFp) => {
      const parsedFp: FundPerformance = this.parsedFPs.find((fp) => fp.period === persistedFp.period);
      persistedFp.period = parsedFp.period;
      persistedFp.totalCommitment = parsedFp.totalCommitment;
      persistedFp.contribution = parsedFp.contribution;
      persistedFp.totalFmv = parsedFp.totalFmv;
      persistedFp.totalExpenses = parsedFp.totalExpenses;
      persistedFp.mgmFees = parsedFp.mgmFees;
      persistedFp.aggregatedOrgExpenses = parsedFp.aggregatedOrgExpenses;
      persistedFp.distribution = parsedFp.distribution;
      persistedFp.navBeforeCarry = parsedFp.navBeforeCarry;
      persistedFp.netIrr = parsedFp.netIrr;
      persistedFp.carryInterest = parsedFp.carryInterest;
      persistedFp.netIrrAfterCarry = parsedFp.netIrrAfterCarry;
    });
    return persistedFPs;
  }

  private async getFundPerformances(): Promise<void> {
    this.fps = await firstValueFrom(this.fpService.getAllFundPerformancesByFundUuid(
      this.selectedOrg.selectedFund.uuid, true, true));
    this.is.uploadDisabled = false;
  }

}

class ShowFPBulkUpload extends DefaultBulkUploadShow {
  periods: {
    name: string;
    isNew: boolean;
  }[];
}
